import {
  ShoppingCart,
  Ticket,
  Settings as SettingsIcon,
  BookOpen,
  FolderOpen,
  Users,
  User,
  Video,
  FileText,
} from "lucide-react";
import { CourseList } from "@/views/admin/CourseList";
import { CourseBuilder } from "@/views/admin/CourseBuilder";
import { BundleBuilder } from "@/views/admin/BundleBuilder";
import { Students } from "@/views/admin/Students";
import { Instructors } from "@/views/admin/Instructors";
import { Zoom } from "@/views/admin/Zoom";
import { SettingsPanel } from "@/views/admin/Settings";
import { Categories } from "@/views/admin/Categories";
import { Orders } from "@/views/admin/Orders";
import { Coupons } from "@/views/admin/Coupons";
import { ModelTestsManager } from "@/views/admin/ModelTestsManager";
import { ToastProvider } from "@/components/admin/ui/Toast";
import { courses } from "@/data/adminMock";
import logo from "@/assets/logo.png";

import { useState, useEffect } from "react";

type AdminView =
  | "courses"
  | "categories"
  | "course-builder"
  | "bundle-builder"
  | "orders"
  | "coupons"
  | "students"
  | "instructors"
  | "zoom"
  | "settings"
  | "model-tests";
 
interface AdminDashboardProps {
  onBack: () => void;
  isDarkMode: boolean;
}
 
export default function AdminDashboard({ onBack: _onBack, isDarkMode: _isDarkMode }: AdminDashboardProps) {
  const [currentView, setCurrentView] = useState<AdminView>("courses");
  const [courseList, setCourseList] = useState<any[]>(courses);
  const [editingCourseId, setEditingCourseId] = useState<number | null>(null);
  const [editingBundleId, setEditingBundleId] = useState<number | null>(null);
  const [showOnlyBundles, setShowOnlyBundles] = useState(false);
  const [_isLoadingCourses, setIsLoadingCourses] = useState(false);

  // Read URL query parameters on mount to hydrate view states
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const page = params.get("page");
    const view = params.get("view") as AdminView;
    const id = params.get("id");

    if (page === "shuddhoshikha-lms-bundles") {
      setShowOnlyBundles(true);
      if (view === "bundle-builder") {
        setCurrentView("bundle-builder");
        if (id) setEditingBundleId(Number(id));
      } else {
        setCurrentView("courses");
      }
    } else if (page === "shuddhoshikha-lms-orders") {
      setCurrentView("orders");
    } else if (page === "shuddhoshikha-lms-coupons") {
      setCurrentView("coupons");
    } else if (page === "shuddhoshikha-lms-model-tests") {
      setCurrentView("model-tests");
    } else if (view && ["courses", "categories", "orders", "coupons", "students", "instructors", "zoom", "settings", "course-builder", "bundle-builder", "model-tests"].includes(view)) {
      setCurrentView(view);
      if (view === "bundle-builder" && id) {
        setEditingBundleId(Number(id));
      } else if (view === "course-builder" && id) {
        setEditingCourseId(Number(id));
      }
    }
  }, []);

  // Fetch real database courses/bundles when view is "courses"
  useEffect(() => {
    if (currentView !== "courses") return;

    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;

    if (!ajaxUrl || !(window as any).jQuery) return;

    setIsLoadingCourses(true);
    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_get_admin_courses",
        nonce: adminNonce,
      },
      success: (res: any) => {
        setIsLoadingCourses(false);
        if (res.success && Array.isArray(res.data)) {
          setCourseList(res.data);
        }
      },
      error: (err: any) => {
        setIsLoadingCourses(false);
        console.error("Failed to load admin courses:", err);
      },
    });
  }, [currentView]);

  const navigateTo = (view: AdminView, id: number | null = null) => {
    setCurrentView(view);
    const params = new URLSearchParams(window.location.search);
    params.set("view", view);
    if (id) {
      params.set("id", id.toString());
    } else {
      params.delete("id");
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ path: newUrl }, "", newUrl);
  };

  const goBack = () => {
    setCurrentView("courses");
    setEditingCourseId(null);
    setEditingBundleId(null);
    const params = new URLSearchParams(window.location.search);
    params.delete("view");
    params.delete("id");
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ path: newUrl }, "", newUrl);
  };
 
  const shLmsAdmin = (window as any).shLmsAdmin;
  const isAdmin = shLmsAdmin?.user?.is_admin || false;
  const isInstructor = (shLmsAdmin?.user?.is_instructor || false) && !isAdmin;

  const horizontalTabs = [
    { key: "courses" as AdminView, label: "Courses", icon: BookOpen },
    ...( ! isInstructor ? [
      { key: "categories" as AdminView, label: "Categories", icon: FolderOpen },
      { key: "orders" as AdminView, label: "Orders", icon: ShoppingCart },
      { key: "coupons" as AdminView, label: "Coupons", icon: Ticket },
      { key: "model-tests" as AdminView, label: "Model Tests", icon: FileText },
    ] : [] ),
    { key: "students" as AdminView, label: "Students", icon: Users },
    ...( ! isInstructor ? [
      { key: "instructors" as AdminView, label: "Instructors", icon: User },
    ] : [] ),
    { key: "zoom" as AdminView, label: "Zoom", icon: Video },
    { key: "settings" as AdminView, label: "Settings", icon: SettingsIcon },
  ];

  // Full-screen views — bypass the sidebar layout
  if (currentView === "course-builder") {
    const courseData = editingCourseId ? courseList.find((c) => c.id === editingCourseId) : null;
    return (
      <ToastProvider>
        <CourseBuilder
          courseId={editingCourseId}
          courseData={courseData}
          onClose={goBack}
          onSave={(newCourse) => {
            if (editingCourseId) {
              setCourseList((prev) =>
                prev.map((c) => (c.id === editingCourseId ? { ...c, ...newCourse } : c))
              );
            } else {
              setCourseList((prev) => [newCourse, ...prev]);
            }
            goBack();
          }}
        />
      </ToastProvider>
    );
  }

  if (currentView === "bundle-builder") {
    return (
      <ToastProvider>
        <BundleBuilder
          bundleId={editingBundleId}
          onClose={goBack}
          onSaveSuccess={(newBundleId: number) => {
            navigateTo("bundle-builder", newBundleId);
            setEditingBundleId(newBundleId);
          }}
        />
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <div className="flex flex-col h-full w-full bg-[#F9FAFB] text-[#1A1F2C] transition-colors font-english overflow-hidden">
        {/* Horizontal Top Header */}
        <header className="bg-white border-b border-slate-200/80 sticky top-0 z-50 flex-shrink-0">
          {/* Row 1: Logo & Profile */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100">
            {/* Left: Branding */}
            <div className="flex items-center gap-3">
              <img src={logo} alt="শুদ্ধশিক্ষা" className="w-[32px] h-[32px] rounded-lg object-cover" />
              <div className="flex flex-col justify-center items-start min-w-0">
                <span className="font-bold text-slate-800 text-[14px] leading-tight block font-bangla">
                  LMS Admin
                </span>
                <span className="text-[9px] text-slate-400 font-semibold block leading-none mt-0.5">
                  Shuddhoshikha Hub
                </span>
              </div>
            </div>

            {/* Right: User Profile */}
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-end min-w-0">
                <span className="font-semibold text-slate-800 text-[12px] leading-tight block">
                  {shLmsAdmin?.user?.display_name || "Admin User"}
                </span>
                <span className="text-[10px] text-slate-400 font-medium block leading-none mt-1">
                  {shLmsAdmin?.user?.user_email || "admin@lms.com"}
                </span>
              </div>
            </div>
          </div>

          {/* Row 2: Scrollable Tabs */}
          <div className="px-6 py-2 bg-slate-50/50 flex items-center overflow-x-auto scrollbar-none">
            <nav className="flex items-center gap-1.5 md:gap-3 pr-4 flex-nowrap whitespace-nowrap">
              {horizontalTabs.map((tab) => {
                const Icon = tab.icon;
                const active = currentView === tab.key;
                
                // Settings active class
                const activeClass = tab.key === "settings"
                  ? "border border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/5 hover:bg-[#FF3B30]/10"
                  : "border border-[#00CBA9] text-[#00CBA9] bg-[#00CBA9]/5 hover:bg-[#00CBA9]/10";

                return (
                  <button
                    key={tab.key}
                    onClick={() => {
                      if (tab.key === "courses") {
                        setShowOnlyBundles(false);
                      }
                      navigateTo(tab.key);
                    }}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer focus:outline-none focus-visible:outline-none flex-shrink-0 ${
                      active
                        ? activeClass
                        : "border border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${active && tab.key === "settings" ? "text-[#FF3B30]" : active ? "text-[#00CBA9]" : "text-slate-400 group-hover:text-slate-500"}`} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </header>

        {/* Main Content Area */}
        <div className="flex-1 overflow-hidden bg-[#F9FAFB]">
          <main className="h-full overflow-y-auto px-6 lg:px-8 py-6 w-full">
            {currentView === "courses" && (
              <CourseList
                showOnlyBundles={showOnlyBundles}
                onNewCourse={() => {
                  setEditingCourseId(null);
                  navigateTo("course-builder");
                }}
                onEditCourse={(id) => {
                  const item = courseList.find((c) => c.id === id);
                  if (item && (item.isBundle || item.post_type === "course_bundle")) {
                    setEditingBundleId(id);
                    navigateTo("bundle-builder", id);
                  } else {
                    setEditingCourseId(id);
                    navigateTo("course-builder", id);
                  }
                }}
                onNewBundle={() => navigateTo("bundle-builder")}
                courseList={courseList}
                setCourseList={setCourseList}
              />
            )}
            {currentView === "categories" && <Categories />}
            {currentView === "orders" && <Orders />}
            {currentView === "coupons" && <Coupons />}
            {currentView === "students" && <Students />}
            {currentView === "instructors" && <Instructors />}
            {currentView === "zoom" && <Zoom />}
            {currentView === "settings" && <SettingsPanel />}
            {currentView === "model-tests" && <ModelTestsManager />}
          </main>
        </div>
      </div>
    </ToastProvider>
  );
}
